// src/pages/HomePage.js
// Updated: 10/17/2025 12:17 PM

import React, { useState, useEffect } from 'react';
import Layout from '../components/common/layout/Layout';
import { useFamily } from '../context/FamilyContext';
import { Link } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, query, onSnapshot, orderBy, where, Timestamp, limit } from 'firebase/firestore'; // Import limit
import { format } from 'date-fns';
import { FaCalendarAlt, FaMapMarkerAlt, FaCalendarPlus, FaBullhorn } from 'react-icons/fa';

const HomePage = () => {
  const { familyData, isHost } = useFamily();
  const [events, setEvents] = useState([]);
  const [latestAnnouncement, setLatestAnnouncement] = useState(null); // State for announcement
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!familyData) return;
    // Fetch upcoming events
    const eventsRef = collection(db, `families/${familyData.id}/events`);
    const qEvents = query(eventsRef, where('startDate', '>=', Timestamp.now()), orderBy('startDate', 'asc'));
    const unsubEvents = onSnapshot(qEvents, (snap) => { setEvents(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))); setLoading(false); });
    
    // Fetch latest announcement
    const announcementsRef = collection(db, `families/${familyData.id}/announcements`);
    const qAnnouncements = query(announcementsRef, orderBy('createdAt', 'desc'), limit(1));
    const unsubAnnouncements = onSnapshot(qAnnouncements, (snap) => {
      setLatestAnnouncement(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() });
    });

    return () => { unsubEvents(); unsubAnnouncements(); };
  }, [familyData]);

  return (
    <Layout>
      <div className="px-4 py-6 sm:px-0">
        {/* NEW: Latest Announcement Banner */}
        {latestAnnouncement && (
          <div className="bg-blue-100 border-l-4 border-primary text-blue-800 p-4 rounded-md mb-8" role="alert">
            <div className="flex items-center gap-3">
              <FaBullhorn className="text-primary"/>
              <div>
                <p className="font-bold">{latestAnnouncement.title}</p>
                <p className="text-sm">{latestAnnouncement.message}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white p-6 rounded-lg shadow-md"><h1 className="text-3xl font-bold text-neutral-gray">Welcome to the {familyData?.familyName} Hub!</h1><p className="mt-2 text-gray-600">All your family reunion information, all in one place.</p></div>
        <div className="mt-8">
          <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-semibold text-neutral-gray">Upcoming Events</h2>{isHost && (<Link to="/dashboard" className="px-4 py-2 bg-cta-orange text-white rounded-md hover:bg-orange-700 text-sm font-medium shadow">Manage Events</Link>)}</div>
          {loading ? (<div className="bg-white p-6 rounded-lg shadow-md text-center"><p className="text-gray-500">Loading events...</p></div>) : events.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{events.map(event => (<div key={event.id} className="bg-white rounded-lg shadow-md overflow-hidden transform hover:-translate-y-1 transition-transform duration-300"><div className="p-6"><h3 className="text-xl font-bold text-primary">{event.eventName}</h3><div className="text-gray-500 text-sm mt-2 space-y-2"><p className="flex items-center gap-2"><FaCalendarAlt /><span>{format(event.startDate.toDate(), 'MMMM d, yyyy')}</span></p><p className="flex items-center gap-2"><FaMapMarkerAlt /><span>{event.location}</span></p></div><p className="mt-4 text-gray-600 line-clamp-3">{event.description}</p></div><div className="bg-gray-50 px-6 py-4"><Link to={`/event/${event.id}`} className="font-semibold text-primary hover:text-blue-700">View Details &rarr;</Link></div></div>))}</div>
          ) : (<div className="bg-white p-6 rounded-lg shadow-md text-center py-12"><FaCalendarPlus className="mx-auto text-5xl text-gray-300"/><h3 className="mt-4 text-lg font-medium text-gray-800">No Upcoming Events</h3>{isHost ? (<p className="text-gray-500 mt-2">Head to the dashboard to create one and get the planning started!</p>) : (<p className="text-gray-500 mt-2">Check back soon for reunion updates from your family host.</p>)}</div>)}
        </div>
      </div>
    </Layout>
  );
};

export default HomePage;